import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ErrorAlert,
  Form,
  FormattedRichMessage,
  UsernameInput,
  USERNAME_VARIANTS,
  useRealm,
  useRichIntl,
  useCIAMHistory,
  useCIAMContextParam,
  ContentPage,
  CoBrandingButton,
  customStyles,
  REALMS,
} from 'ciam-self-service-shared';
import submitUsernameAsyncAction from 'utils/forgotPasswordAuthnTree/submitUsernameAsyncAction';
import { useDispatch, useSelector } from 'react-redux';
import initAsyncAction from 'utils/forgotPasswordAuthnTree/initAsyncActionImpl';
import { updateErrorCode } from 'components/Login/actions';
import { FORGOT_PASSWORD_TREE } from 'components/Login/constants';
import useAuthTreeErrorHandler from 'utils/authTree/useAuthTreeErrorHandler';
import { useLocation } from 'react-router-dom';
import Page, { AUTH_TYPE } from '../../common/Page';
import FormElement from '../../common/FormElement';
import AnalyticsDataLayer from '../../common/Analytics';
import {
  FP_PROMPT_PAGE_NAME,
  FP_PROMPT_CHANGE_PASSWORDPAGE_NAME,
  EVENT_ENTER_USERNAME_ERROR,
  forgotPasswordTransaction,
} from '../constants';
import InternetExplorerUserWarning from '../../common/InternetExplorerUserWarning';
import { changePasswordUpdateStatus, updateMfaPage } from '../actions';
import useAdobeAnalyticsTransaction from '../../../hooks/useAdobeAnalyticsTransaction';
import { TRANSACTION_START } from 'utils/constants';

const ForgotPasswordPrompt = () => {
  const intl = useRichIntl();
  const realm = useRealm();
  const stage = useSelector((state) => state.loginInfo.stage);
  const history = useCIAMHistory();
  const dispatch = useDispatch();
  const [username, setUsername] = useState('');
  const error = useSelector((state) => state.loginInfo.loginError);
  const brand = useCIAMContextParam('brand');
  const requestInProgress = useSelector((state) => state.forgotPasswordInfo.passwordUpdateStatus);
  const authTreeErrorHandler = useAuthTreeErrorHandler();
  const location = useLocation();

  // Determine if we are displaying the change password page based on realm and pathname
  const displayChangePasswordPage =
    (realm === REALMS.CUSTOMER && location.pathname === '/changepassword') ||
    location.pathname === `/${realm}/changepassword`;

  useAdobeAnalyticsTransaction(TRANSACTION_START, forgotPasswordTransaction, error, 1); // 1 is the step number to be passed into adobe analytics

  const handleSubmit = async (event) => {
    event.preventDefault();
    dispatch(updateMfaPage({ mfaDetail: true }));
    const payload = {
      userName: username,
      stage,
      history,
      realm,
      brand,
    };
    try {
      dispatch(changePasswordUpdateStatus(true));
      await submitUsernameAsyncAction(dispatch, payload, 2);
    } catch (err) {
      authTreeErrorHandler(err);
    } finally {
      dispatch(changePasswordUpdateStatus(false));
    }
  };

  useEffect(() => {
    const showError = location?.state?.showError;
    initAsyncAction(dispatch, FORGOT_PASSWORD_TREE, realm, brand, showError);
  }, [realm, brand, dispatch, location]);

  useEffect(() => {
    dispatch(updateMfaPage({ mfaDetail: false }));
    return () => {
      // reset all errors on component unload
      dispatch(updateErrorCode(null));
    };
  }, [dispatch]);

  const handleUsernameChange = (value) => {
    setUsername(value);
    if (error?.component === 'username') {
      dispatch(updateErrorCode(null));
    }
  };

  const pageTitle = displayChangePasswordPage
    ? intl.formatMessage({ id: 'forgotPassword.prompt.page.changePasswordTitle' })
    : intl.formatMessage({ id: 'forgotPassword.prompt.page.title' });

  const pageHeading = displayChangePasswordPage ? (
    <FormattedMessage id="forgotPassword.prompt.page.changePasswordHeading" />
  ) : (
    <FormattedMessage id="forgotPassword.prompt.page.heading" />
  );

  const analyticsPageName = displayChangePasswordPage ? FP_PROMPT_CHANGE_PASSWORDPAGE_NAME : FP_PROMPT_PAGE_NAME;

  return (
    <Page
      title={pageTitle}
      authType={AUTH_TYPE.UNAUTHENTICATED}
      description={intl.formatMessage({ id: 'forgotPassword.prompt.page.meta.description' })}
    >
      {error && <AnalyticsDataLayer eventName={EVENT_ENTER_USERNAME_ERROR} error={error} />}
      {!error && <AnalyticsDataLayer pageName={analyticsPageName} />}
      <ContentPage heading={pageHeading}>
        <InternetExplorerUserWarning />
        <ErrorAlert error={error} />
        <FormattedRichMessage id="forgotPassword.prompt.page.content" />
        <Form onSubmit={handleSubmit}>
          <FormElement>
            <UsernameInput
              variant={USERNAME_VARIANTS.PROMPT}
              id="username"
              value={username}
              onChange={handleUsernameChange}
              errors={[error]}
              required
              ariaRequired
              autoFocus
              disableValidateOnBlur
            />
          </FormElement>
          <FormElement>
            <CoBrandingButton
              id="fp-username-continue"
              type="submit"
              customStyle={customStyles.button}
              disabled={requestInProgress}
              saving={requestInProgress}
              analyticsData={{
                click: {
                  linkName: intl.formatMessage({ id: 'forgotPassword.prompt.submit' }),
                  linkHref: '/mfa',
                },
              }}
            >
              <FormattedMessage id="forgotPassword.prompt.submit" />
            </CoBrandingButton>
          </FormElement>
        </Form>
      </ContentPage>
    </Page>
  );
};

export default ForgotPasswordPrompt;
